<script>
import {getColorIconFromMIME, getFontAwesomeIconFromMIME} from '@utils/FontAwesomeMimeType';

/* COMPOSANTS */
import FileUpload from 'primevue/fileupload';
import AkFicCard from '@components/v2/general/AkFicCard.vue';
import AkDialogDelete from '@components/general/AkDialogDelete.vue';

/* MIXINS */
import fileUploadMixin from '@mixins/fileUploadMixin';
import messageMixin from '@mixins/messageMixin';

/* SERVICES */
import ficService from '@services/ficService';

export default {
  components: {AkDialogDelete, AkFicCard, FileUpload},
  mixins: [fileUploadMixin, messageMixin],
  props: {
    modelValue: {
      required: true,
    },
    tableName: {
      type: String,
      required: true,
    },
    tableId: {
      type: [Number, String],
      required: true,
    },
    ficType: {
      type: String,
      required: false,
      default: undefined,
    },
    accept: {
      type: String,
      required: false,
      default: '',
    },
    ficPreview: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    canDelete: {
      type: Boolean,
      required: false,
      default: true,
    },
    canUpload: {
      type: Boolean,
      required: false,
      default: true,
    },
    title: {
      type: String,
      required: false,
      default: undefined,
    },
    gridLayout: {
      type: Boolean,
      required: false,
      default: true,
    },
    bgClass: {
      type: String,
      required: false,
      default: '',
    },
  },
  emits: ['update:modelValue', 'added', 'removed'],
  data() {
    return {
      fileAction: null,
      displayDeleteDialog: false,
      showErrorMessage: false,
      messages: null,
    };
  },
  computed: {
    currentValue: {
      get: function () {
        return this.modelValue;
      },
      set: function (val) {
        this.$emit('update:modelValue', val);
      },
    },
  },
  methods: {
    getFileIcon(fileName) {
      return getFontAwesomeIconFromMIME(fileName);
    },
    getColorIcon(fileName) {
      return getColorIconFromMIME(fileName);
    },
    downloadFile(file, isPreview) {
      ficService.download(file, isPreview).catch((e) => {
        if (e.response.status === 404) {
          this.addError("Le fichier n'existe pas");
        }
      });
    },
    selectFiles(event) {
      if (event.files && event.files.length > 0) {
        let fic = {};
        fic.tableName = this.tableName;
        fic.tableId = this.tableId;
        if (this.ficType) {
          fic.ficType = this.ficType;
        }
        if (this.ficItemLabelId) {
          fic.ficItemLabelId = this.ficItemLabelId;
        }
        if (this.uniqueFic) {
          fic.uniqueFic = this.uniqueFic;
        }

        ficService.createItems(fic, event.files).then((e) => {
          Array.prototype.push.apply(this.currentValue, e);
          this.$refs.uploader.clear();
          this.$emit('added');
        });
      }
      if (this.$refs.uploader.messages && this.$refs.uploader.messages.length) {
        this.showErrorMessage = true;
        this.messages = this.$refs.uploader.messages.map((message) => message).join(' ');
        this.$refs.uploader.onMessageClose();
      }
    },
    confirmDeleteFile(file) {
      this.showErrorMessage = false;
      this.messages = null;
      this.fileAction = file;
      this.$refs.dialogDelete.show();
    },
    deleteFile() {
      this.showErrorMessage = false;
      this.messages = null;
      this.$refs.uploader.onMessageClose();
      ficService
        .delete(this.fileAction)
        .then(() => {
          this.currentValue = this.currentValue.filter((val) => val.id !== this.fileAction.id);
          this.success('Le fichier a bien été supprimé');
          this.$emit('removed');
        })
        .catch(() => {
          this.addError('Une erreur est survenue, merci de réessayer plus tard !');
        })
        .finally(() => {
          this.fileAction = undefined;
          this.$refs.dialogDelete.hide();
        });
    },
    uploadFile() {
      this.showErrorMessage = false;
      this.messages = null;
      this.$refs.uploader.onMessageClose();
      this.$refs.uploader.$refs.fileInput.click();
    },
  },
};
</script>

<template>
  <div class="flex justify-between items-center">
    <span class="text-grey-900 text-md font-semibold">{{ title }}</span>
    <i
      class="ga-icon ga-plus_circle text-grey-900 text-3xl hover:cursor-pointer"
      v-if="canUpload"
      @click="uploadFile"></i>
  </div>
  <div
    :class="{'grid md:grid-cols-3 sm:grid-cols-2 grid-cols-1': gridLayout, 'flex flex-col': !gridLayout}"
    class="gap-2 mt-4"
    v-if="currentValue && currentValue.length > 0">
    <AkFicCard
      v-for="(item, idx) in currentValue"
      v-model="currentValue[idx]"
      :disabled="!canDelete"
      :key="item.id"
      custom-delete
      :reference="item.id"
      :table-id="item.id"
      :title="item.fileName"
      @delete-item="confirmDeleteFile(item)"
      :table-name="tableName"
      :ficPreview="ficPreview" />
  </div>
  <FileUpload
    v-if="canUpload"
    ref="uploader"
    :invalidFileSizeMessage="this.getInvalidFileSizeMessage()"
    :invalidFileTypeMessage="this.getInvalidFileTypeMessage(accept)"
    :maxFileSize="getMaxFileUpload()"
    :accept="accept"
    multiple
    @select="selectFiles">
    <template #header>
      <div></div>
    </template>
    <template #content="{files}">
      <div
        v-if="files.length > 0"
        class="flex flex-col justify-center items-center gap-1 p-10 border-dashed rounded-md border border-grey-50 bg-white hover:cursor-pointer md:hover:bg-secondary-50 md:hover:border-secondary"
        @click="uploadFile">
        <i class="ga-icon ga-upload text-3xl text-secondary" />
        <p class="text-base font-normal text-grey-900">Faites glisser un fichier ici</p>
        <p class="text-base font-normal text-grey-900">
          Ou <span class="text-grey-900 text-base">importer depuis votre ordinateur</span>
        </p>
      </div>
    </template>
    <template #empty>
      <div :class="bgClass" class="border-none">
        <div
          class="flex flex-col justify-center items-center gap-1 p-10 border-dashed rounded-md border border-grey-50 bg-white hover:cursor-pointer md:hover:bg-secondary-50 md:hover:border-secondary"
          @click="uploadFile">
          <i class="ga-icon ga-upload text-3xl text-secondary" />
          <p class="text-base font-normal text-grey-900">Faites glisser un fichier ici</p>
          <p class="text-base font-normal text-grey-900">
            Ou <span class="text-grey-900 text-base">importer depuis votre ordinateur</span>
          </p>
        </div>
      </div>
    </template>
  </FileUpload>
  <div class="flex flex-col flex-1">
    <small v-if="this.showErrorMessage" class="p-error">
      {{ this.messages }}
    </small>
  </div>

  <AkDialogDelete
    ref="dialogDelete"
    message="Etes-vous sur de vouloir supprimer ce fichier ?"
    title="Suppression de fichier"
    @yes="deleteFile">
  </AkDialogDelete>
</template>
